import { Suspense, useEffect, useState } from 'react'
import PermGate from './PermGate'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Spin } from 'antd'
import { useTranslation } from 'react-i18next'

import apiClient from '../api/client'
import { useAuthStore } from '../stores/auth'
import AdminHeader from './AdminHeader'
import AdminSidebar from './AdminSidebar'
import CommandPalette from './CommandPalette'
import ErrorBoundary from './ErrorBoundary'

// AdminLayout — WebPanel-Pattern: AppLayout holds sidebarOpen, no AntD
// Layout/Sider. Mobile behavior is CSS-only (sidebar slides in via
// transform on ≤900px). All styling in enterprise.css.

const PAGE_TITLE_KEYS: Record<string, { key: string; fallback: string }> = {
  '/': { key: 'nav.dashboard', fallback: 'Dashboard' },
  '/customers': { key: 'nav.customers', fallback: 'Kunden' },
  '/products': { key: 'nav.products', fallback: 'Produkte' },
  '/extensions': { key: 'nav.extensions', fallback: 'Extensions' },
  '/tickets': { key: 'nav.tickets', fallback: 'Tickets' },
  '/dsgvo': { key: 'nav.dsgvo', fallback: 'DSGVO' },
  '/dsgvo-requests': { key: 'nav.dsgvoRequests', fallback: 'DSGVO-Anfragen' },
}

export default function AdminLayout() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)

  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    // Skip re-validation if user is set AND the session hasn't expired yet.
    if (user && new Date(user.expires_at) > new Date()) return
    apiClient
      .get<{ data: { actor: string; role: string; audience: string; expires_at: string; permissions?: string[] } }>(
        '/api/v1/auth/me',
      )
      .then((res) => setUser(res.data.data))
      .catch(() => navigate('/login'))
  }, [user, setUser, navigate])

  // Close drawer when route changes (mobile UX).
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  const title = PAGE_TITLE_KEYS[location.pathname]
  const pageTitle = title ? t(title.key, title.fallback) : undefined

  return (
    <div className="app-layout admin-shell">
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <CommandPalette />
      <main className="main-content">
        <AdminHeader pageTitle={pageTitle} onMenuToggle={() => setSidebarOpen((p) => !p)} />
        <div className="content-area">
          <ErrorBoundary label={location.pathname}>
            <Suspense fallback={
              <div className="flex-center pad-48">
                <Spin size="large" />
              </div>
            }>
              <PermGate><Outlet /></PermGate>
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>
    </div>
  )
}
